import { isArray, isEmpty, isFinite, isNil, isNumber, isString, uniq } from 'lodash';
import { API } from '@shared/types/api';
import { foldAccents } from './phrasePrefix';

export type IndexedValue = string | number | boolean;

/** The parts of a workflow template component that matching reads. */
export interface MatchComponent {
  id: string;
  type: string;
}

// A keyword value longer than this isn't indexed, so it reads as no value at all (`ignore_above`).
export const KEYWORD_MAX_LENGTH = 256;

/**
 * The values core-api's search index holds for a component of a card, or for one of its scalar
 * representations - or `undefined` when the client doesn't know how core-api derives them. An
 * empty list is a missing field, which is what `isEmpty` matches. An empty string is a value.
 */
export function getIndexedValues(
  card: API.WorkflowEntity,
  component: MatchComponent,
  scalarKey: API.ScalarKey | null | undefined,
): IndexedValue[] | undefined {
  const fieldValue = getFieldValue(card, component.id);
  if (!isNil(scalarKey)) {
    return getScalarValues(scalarKey, fieldValue);
  }
  switch (component.type) {
    case 'TEXT':
      return isString(fieldValue) ? [fieldValue] : [];
    case 'TAG':
    case 'ENTITY_BATCH':
      return toList(fieldValue)
        .filter(isString)
        .filter(value => value.length <= KEYWORD_MAX_LENGTH);
    case 'NUMBER':
      return isFinite(fieldValue) ? [fieldValue] : [];
    case 'DATE':
      return isDateValue(fieldValue) ? [fieldValue] : [];
    case 'PERSON':
      return toList(fieldValue).filter(isNumber);
    case 'RELATED_CARD':
      return getRelatedCardIds(fieldValue);
    default:
      return undefined;
  }
}

// Mirrors core-api's scalar definitions (`packages/domain/src/components/behaviors/*/scalars`).
function getScalarValues(scalarKey: API.ScalarKey, fieldValue: any): IndexedValue[] | undefined {
  switch (scalarKey) {
    case 'EMAIL_VALUE_STRING':
      return toList(fieldValue).filter(isString);
    case 'FILE_NAMES_STRING':
      return toList(fieldValue)
        .map(file => file?.name)
        .filter(isString);
    case 'URL_VALUE_STRING':
      return toList(fieldValue)
        .map(url => url?.url)
        .filter(isString);
    case 'RELATED_CARD_IDS':
      return getRelatedCardIds(fieldValue);
    case 'SIGNATURE_SIGNED_BY_USER_ID':
      return isNumber(fieldValue?.userId) ? [fieldValue.userId] : [];
    case 'SIGNATURE_SIGNED_DATE':
      return isDateValue(fieldValue?.date) ? [fieldValue.date] : [];
    case 'TIME_TRACKER_IS_ACTIVE_BOOLEAN':
      if (isNil(fieldValue)) {
        return [];
      }
      if (!isArray(fieldValue)) {
        return undefined;
      }
      // core-api counts a session as running only while its `end` is absent, so one stored with
      // `end: null` reads as stopped
      return [
        fieldValue.some(session => session?.start !== undefined && session?.end === undefined),
      ];
    default:
      return undefined;
  }
}

/**
 * A card's value for a component. core-api indexes the card's own properties with its fields laid
 * over them, so `createdAt` and `updatedAt` fall back to the card's when the fields don't hold them.
 */
export function getFieldValue(card: API.WorkflowEntity, componentId: string): any {
  if (!isNil(card.fields) && componentId in card.fields) {
    return card.fields[componentId];
  }
  switch (componentId) {
    case 'createdAt':
      return card.createdAt;
    case 'updatedAt':
      return card.updatedAt;
    default:
      return undefined;
  }
}

/** The ids of the cards a related card field links to, as core-api indexes and traverses them. */
export function getRelatedCardIds(fieldValue: unknown): string[] {
  return uniq(
    toList(fieldValue)
      .map(item => item?.id)
      .filter(isString)
      .filter(id => !isEmpty(id)),
  );
}

/** A keyword field's value as core-api's `lowercase_normalizer` compares it. */
export function normalizeKeyword(value: string): string {
  return foldAccents(value.toLowerCase());
}

export function getTime(value: IndexedValue): number {
  return new Date(value as string).getTime();
}

function isDateValue(value: unknown): value is string {
  return isString(value) && !isNaN(getTime(value));
}

function toList(value: any): any[] {
  if (isNil(value)) {
    return [];
  }
  return isArray(value) ? value : [value];
}
